/**
 * Apps Configuration
 *
 * This file provides app configurations for search functionality and navigation.
 * It derives data from the centralized MODULE_REGISTRY to maintain a single source of truth.
 *
 * @see modules.ts for the canonical module definitions
 */

import {
  MODULE_REGISTRY,
  getRouteToModuleMap,
  getModulesByCategory,
  type ModuleDefinition,
} from "./modules";

/**
 * App configuration for search and navigation
 */
export interface AppConfig {
  id: string;
  title: string;
  description: string;
  path: string;
  module: string;
  icon: any;
  category: "navigation" | "app";
  searchType:
    | "dashboard"
    | "screener"
    | "playground"
    | "documents"
    | "ops"
    | "app";
}

/**
 * Convert a module definition to an app config
 */
function moduleToAppConfig(module: ModuleDefinition): AppConfig {
  const routeSegment = module.routePath.replace(/^\//, "");
  const searchType = getSearchType(module);

  return {
    id: routeSegment,
    title: module.label,
    description: module.description,
    path: module.routePath,
    module: module.id,
    icon: module.icon,
    category: module.category === "specialized" ? "app" : "navigation",
    searchType,
  };
}

/**
 * Determine the search type for a module
 */
function getSearchType(
  module: ModuleDefinition
): AppConfig["searchType"] {
  if (module.category === "specialized") {
    return "app";
  }
  switch (module.id) {
    case "dashboard":
      return "dashboard";
    case "screener":
      return "screener";
    case "playground":
      return "playground";
    case "documents":
      return "documents";
    case "ops":
      return "ops";
    default:
      return "app";
  }
}

/**
 * Main navigation apps (core modules with showInNavigation)
 * These appear in the main sidebar navigation
 */
export const navigationApps: AppConfig[] = getModulesByCategory("core")
  .filter((m) => m.showInNavigation)
  .map(moduleToAppConfig);

/**
 * Specialized apps (shown in Apps section)
 * These appear in the Apps popover/section
 */
export const specializedApps: AppConfig[] = getModulesByCategory("specialized")
  .filter((m) => m.showInAppsSection)
  .map(moduleToAppConfig);

/**
 * All apps combined (navigation + specialized)
 */
export const allApps: AppConfig[] = [...navigationApps, ...specializedApps];

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get apps filtered by category
 */
export function getAppsByCategory(category: "navigation" | "app"): AppConfig[] {
  return allApps.filter((app) => app.category === category);
}

/**
 * Get all apps the user has permission for
 */
export function getAppsByPermission(
  hasPermission: (module: string) => boolean
): AppConfig[] {
  return allApps.filter((app) => hasPermission(app.module));
}

/**
 * Get navigation apps the user has permission for
 */
export function getNavigationAppsByPermission(
  hasPermission: (module: string) => boolean
): AppConfig[] {
  return navigationApps.filter((app) => hasPermission(app.module));
}

/**
 * Get specialized apps the user has permission for
 */
export function getSpecializedAppsByPermission(
  hasPermission: (module: string) => boolean
): AppConfig[] {
  return specializedApps.filter((app) => hasPermission(app.module));
}

/**
 * Route to module mapping
 * Re-exported from modules.ts for backward compatibility
 */
export const routeModules: Record<string, string> = getRouteToModuleMap();
